import { KeyRound, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardBody, CardHeader } from '@crm/ui';
import { PERMISSIONS, isPermissionCode } from '@crm/contracts';
import { runKeycloakAction } from '../auth/oidc.ts';
import { setLanguage } from '../i18n/index.ts';
import { useTenant } from '../tenant/TenantProvider.tsx';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="col-span-2 text-slate-900">{children}</dd>
    </div>
  );
}

export function MyAccountPage() {
  const { t, i18n } = useTranslation();
  const { me, context, tenantId, switchTenant } = useTenant();
  const lang = i18n.language === 'en' ? 'en' : 'fr';

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-bold text-brand">{t('myAccount.title')}</h1>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('myAccount.profile')} />
          <CardBody>
            <dl className="divide-y divide-slate-100">
              <Row label={t('myAccount.email')}>{me.email}</Row>
              <Row label={t('myAccount.phone')}>{me.phone ?? '—'}</Row>
              <Row label={t('myAccount.accessType')}>{t(`access.${me.access_type}`)}</Row>
              {me.is_platform_admin ? (
                <Row label=" ">
                  <Badge tone="brand">{t('myAccount.platformAdmin')}</Badge>
                </Row>
              ) : null}
              <Row label={t('myAccount.language')}>
                <select
                  value={lang}
                  onChange={(e) => setLanguage(e.target.value as 'fr' | 'en')}
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </Row>
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('myAccount.security')} />
          <CardBody className="space-y-3">
            <Button variant="secondary" className="w-full sm:w-auto" onClick={() => void runKeycloakAction('UPDATE_PASSWORD', '/compte')}>
              <KeyRound size={16} /> {t('myAccount.changePassword')}
            </Button>
            <div>
              <Button variant="secondary" className="w-full sm:w-auto" onClick={() => void runKeycloakAction('CONFIGURE_TOTP', '/compte')}>
                <ShieldCheck size={16} /> {t('myAccount.enableOtp')}
              </Button>
              <p className="mt-2 text-xs text-slate-500">{t('myAccount.otpHelp')}</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('myAccount.tenants')} />
          <CardBody>
            <ul className="space-y-2">
              {me.memberships.map((m) => (
                <li key={m.tenant_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2">
                  <span>
                    <span className="block text-sm font-semibold">{m.tenant_name}</span>
                    <span className="block text-xs text-slate-500">{m.role_name}</span>
                  </span>
                  {m.tenant_id === tenantId ? (
                    <Badge tone="success">{t('myAccount.current')}</Badge>
                  ) : (
                    <Button variant="ghost" className="h-8 text-brand" onClick={() => switchTenant(m.tenant_id)}>
                      {t('header.switchTenant')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('myAccount.permissions')} subtitle={`${context.tenant.name} — ${context.role.name}`} />
          <CardBody>
            <ul className="flex flex-wrap gap-1.5">
              {context.permissions.map((p) => (
                <li key={p}>
                  <Badge tone="brand" title={p}>{isPermissionCode(p) ? PERMISSIONS[p][lang] : p}</Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
